import React, { useState } from "react";
import { connect } from "react-redux";

import { ColoredButton, WhiteButton } from "../../styledComponents/buttons";
import { createNotification } from "../../modules/notificationManager";
import CompareStrategiesModal from "./CompareStrategiesModal";
import DeleteRouteModal from "./DeleteRouteModal";
import RouteMapView from "./RouteMapView";
import {
  compareRouteStrategies,
  deleteRoute,
  lockRoute,
  setSelectedRoute,
} from "./routeOptimizationActions";
import {
  JobAddress,
  JobInfo,
  JobItem,
  JobName,
  JobSequence,
  JobStatus,
  JobsList,
  LockBadge,
  RouteActions,
  RouteBadge,
  RouteCard,
  RouteCardHeader,
  RouteEmployeeName,
  RouteStat,
  RouteStatLabel,
  RouteStatValue,
  RouteStats,
} from "./routeOptimizationStyles";
import StatusWithDot from "components/StatusWithDot/StatusWithDot";

function RouteCardComponent({
  route,
  organizationId,
  lockRoute,
  deleteRoute,
  compareRouteStrategies,
  isLocking,
  isComparing,
  isDeleting,
  selectedOrganization,
}) {
  const orgType = selectedOrganization?.type || "operation";
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [showMapModal, setShowMapModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleToggleLock = () => {
    if (isLocking) return;
    lockRoute({
      organization_id: organizationId,
      route_id: route.id,
      locked: !route.is_locked,
    });
  };

  const handleCompare = () => {
    if (isComparing || route.is_locked) return;
    compareRouteStrategies({
      organization_id: organizationId,
      route_id: route.id,
    });
    setShowCompareModal(true);
  };

  const handleDeleteClick = () => {
    if (isDeleting || route.is_locked) return;
    
    if (route.is_locked) {
      createNotification("error", "Cannot delete a locked route. Please unlock it first.", 3000);
      return;
    }

    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = () => {
    deleteRoute({
      organization_id: organizationId,
      route_id: route.id,
    });
    setShowDeleteModal(false);
  };

  const handleViewMap = () => {
    if (!route.jobs || route.jobs.length === 0) return;
    setShowMapModal(true);
  };

  const getJobStatus = job => {
    if (job.status === "completed") return "completed";
    if (job.status === "in_progress") return "in_progress";
    return "pending";
  };

  return (
    <>
      <RouteCard>
        <RouteCardHeader>
          <RouteEmployeeName>{route.employee_name}</RouteEmployeeName>
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            <RouteBadge mode={route.mode}>{route.mode}</RouteBadge>
            {route.is_locked && <LockBadge>🔒 Locked</LockBadge>}
          </div>
        </RouteCardHeader>

        <RouteStats>
          <RouteStat>
            <RouteStatLabel>{orgType === "operation" ? "Jobs" : "Tasks"}</RouteStatLabel>
            <RouteStatValue>{route.jobs?.length || 0}</RouteStatValue>
          </RouteStat>
          <RouteStat>
            <RouteStatLabel>Distance</RouteStatLabel>
            <RouteStatValue>
              {route.total_distance_km
                ? `${route.total_distance_km.toFixed(1)} km`
                : "N/A"}
            </RouteStatValue>
          </RouteStat>
          <RouteStat>
            <RouteStatLabel>Duration</RouteStatLabel>
            <RouteStatValue>
              {route.estimated_duration_min
                ? `${route.estimated_duration_min} min`
                : "N/A"}
            </RouteStatValue>
          </RouteStat>
          <RouteStat>
            <RouteStatLabel>Score</RouteStatLabel>
            <RouteStatValue>
              {route.optimization_score
                ? route.optimization_score.toFixed(1)
                : "N/A"}
            </RouteStatValue>
          </RouteStat>
        </RouteStats>

        <JobsList>
          {route.jobs && route.jobs.length > 0 ? (
            route.jobs.map((job, index) => (
              <JobItem key={job.id}>
                <JobSequence>{index + 1}</JobSequence>
                <JobInfo>
                  <JobName>{job.name}</JobName>
                  {job.address && <JobAddress>{job.address}</JobAddress>}
                </JobInfo>
                {/* <JobStatus status={getJobStatus(job)}>
                  {getJobStatus(job)}
                </JobStatus> */}
                <StatusWithDot status={getJobStatus(job)} />
                
              </JobItem>
            ))
          ) : (
            <div
              style={{ textAlign: "center", padding: "20px", color: "#999" }}
            >
              No {orgType === "operation" ? "jobs" : "tasks"} assigned
            </div>
          )}
        </JobsList>

        <RouteActions>
          <ColoredButton
            type="primary"
            onClick={handleViewMap}
            disabled={!route.jobs || route.jobs.length === 0}
            style={{ minWidth: 0, flex: "1 1 auto" }}
          >
            🗺️ View Map
          </ColoredButton>
          <WhiteButton
            onClick={handleToggleLock}
            disabled={isLocking}
            style={{ minWidth: 0, flex: "1 1 auto" }}
          >
            {isLocking
              ? "Processing..."
              : route.is_locked
              ? "🔓 Unlock"
              : "🔒 Lock"}
          </WhiteButton>
          <ColoredButton
            type="blue"
            onClick={handleCompare}
            disabled={isComparing || route.is_locked}
            style={{ minWidth: 0, flex: "1 1 auto" }}
          >
            {isComparing ? "Loading..." : "Compare"}
          </ColoredButton>
          <ColoredButton
            type="danger"
            onClick={handleDeleteClick}
            disabled={isDeleting || route.is_locked}
            style={{ minWidth: 0, flex: "1 1 auto" }}
          >
            {isDeleting ? "Deleting..." : "🗑️ Delete"}
          </ColoredButton>
        </RouteActions>
      </RouteCard>

      {showCompareModal && (
        <CompareStrategiesModal
          routeId={route.id}
          organizationId={organizationId}
          onClose={() => setShowCompareModal(false)}
        />
      )}

      {showMapModal && (
        <RouteMapView route={route} onClose={() => setShowMapModal(false)} />
      )}

      <DeleteRouteModal
        isOpen={showDeleteModal}
        toggle={() => setShowDeleteModal(false)}
        handleDelete={handleDeleteConfirm}
        isLoading={isDeleting}
        employeeName={route.employee_name}
      />
    </>
  );
}

const mapStateToProps = state => ({
  isLocking: state.RouteOptimization.isLocking,
  isComparing: state.RouteOptimization.isComparing,
  isDeleting: state.RouteOptimization.isDeleting,
  selectedOrganization: state.organization.selectedOrganization,
});

const mapDispatchToProps = {
  lockRoute,
  setSelectedRoute,
  compareRouteStrategies,
  deleteRoute,
};

export default connect(mapStateToProps, mapDispatchToProps)(RouteCardComponent);
